<template>
  <div>
    <div class="text-small-medium mb-8 mt-24">
      {{ productResources.getNotifiedTitle }}
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="!showSuccessMessage">
        <TextInput
          :id="'ProductPageOutOfStockInput'"
          :placeholder="'E-postadress'"
          :type="'email'"
          :form-input="true"
          :error="emailError"
          :init-value="userEmail"
          @submit-input="emailInput"
        />
        <div class="flex">
          <button
            ref="stockalertBtn"
            class="btn btn--primary w-full"
            @click="applyStockStatusAlert"
          >
            <span v-if="!showSuccessMessage">{{ buttonText }}</span>
            <span v-else>{{ sharedResources.stockAlertSuccessButton }}</span>
          </button>
          <button
            v-if="!inModal"
            :title="productResources.addToFavourites"
            class="btn btn--secondary ml-16 px-12 flex-shrink- w-40 h-40 md:h-48 md:w-48"
            @click="$emit('add-to-favourites', partNo)">
            <BookmarkIcon class="w-20 h-20 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            <client-only>
              <transition name="fade" mode="out-in">
                <BookmarkIconSolid v-if="isAddedToFavourites" class="w-20 h-20 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </transition>
            </client-only>
          </button>
        </div>
        <WysiwygWrapper
          v-if="productSettings.backInStockPrivacyPolicy"
          :style-config="{
            'wysiwyg wysiwyg--tiny-text mt-12': true,
          }"
          :html="productSettings.backInStockPrivacyPolicy"
        />
      </div>
      <div v-else-if="showSuccessMessage">
        <div class="bg-green rounded-md p-24 mt-12 text-center">
          <CheckCircle
            class="w-32 h-32 mb-12 text-success mx-auto"
          />
          <div class="text-small-regular">{{ sharedResources.stockAlertSuccessButton }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
import { type Ref, ref } from 'vue';
import TextInput from '~/components/form-elements/TextInput.vue';
import { useGlobalContentStore } from '~/store/globalContent';
import CheckCircle from '@heroicons/vue/24/outline/esm/CheckCircleIcon';
import BookmarkIcon from '@heroicons/vue/24/outline/esm/BookmarkIcon';
import BookmarkIconSolid from '@heroicons/vue/24/solid/esm/BookmarkIcon';
import { useFavouritesStore } from '~/store/favourites';
import { type IICommandResultBase } from '~/api-types';
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';

const { apiPost } = useApiFetch();
const productSettings = useGlobalContentStore().productSettings;
const globalContentStore = useGlobalContentStore();
const marketSettings = globalContentStore.marketSettings;
const sharedResources = globalContentStore.sharedResources;
const productResources = globalContentStore.productResources;
const favouritesStore = useFavouritesStore();
const favouritesArray: Ref<string[]> = ref([]);
const stockalertBtn = ref();
const buttonText = ref('');
const uiStore = useUiStore();
const { userEmail } = storeToRefs(uiStore);

favouritesArray.value = favouritesStore.favourites;

const email = ref();
const emailError = ref();
const showSuccessMessage = ref(false);

const props = defineProps<{
  partNo: string,
  isAddedToFavourites: boolean,
  inModal?: boolean,
}>();

defineEmits<{
  (event: 'add-to-favourites'): void,
}>();

onMounted(() => {
  calculateButtonWidth();
  window.addEventListener('resize', calculateButtonWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateButtonWidth);
});

function calculateButtonWidth() {
  const button = stockalertBtn.value;

  if (button && button.offsetWidth) {
    if (button.offsetWidth < 330) {
      buttonText.value = sharedResources.stockAlertButtonMobile;
    } else {
      buttonText.value = sharedResources.stockAlertButton;
    }
  }
}

const emailInput = (input: string) => {
  emailError.value = '';
  email.value = input;

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim()) && input) {
    emailError.value = '';
  } else if (!input) {
    emailError.value = '';
  } else {
    emailError.value = sharedResources.invalidEmail;
  }
};

const applyStockStatusAlert = async() => {
  if (!email.value || emailError.value) {
    emailError.value = sharedResources.invalidEmail;
    return;
  }

  await apiPost<IICommandResultBase>(
    'product/backinstockalert',
    {
      'countryCode': marketSettings.countryCode,
      'partNo': props.partNo,
      'email': email.value,
    }
  );
  email.value = '';
  showSuccessMessage.value = true;

  if (!props.inModal) {
    setTimeout(() => {
      showSuccessMessage.value = false;
    }, 4000);
  }
};

</script>
